import React, { useState } from 'react';
import WarningAmber from '@mui/icons-material/WarningAmber';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import { useHistory, useLocation } from 'react-router-dom';
import { erpSettingsPathFromPathname } from 'pages/Layout/hospitalityModulePaths';
import { useAppSelector } from 'redux/hooks';
import { selectRooms, selectUserEmail } from 'pages/Hospitality/redux';
import { selectLiveView, selectPermission } from 'redux/global';
import { PERM } from 'pages/Settings/redux';
import { formatStorageBytes } from 'pages/Logs/logUtils';
import { useHotelStorage } from 'pages/hooks/useHotelStorage';
import { usePath } from 'pages/hooks';
import { selectRightBarDismissTick } from 'pages/redux';
import { isCheckoutRoomChargeAlertActive, readLiveDismissedRoomChargeIds } from './CheckoutRoomChargeAlert';
import { isManagerReportsAlertActive } from './ManagerReportsAlert';
import { selectManagerReports } from 'pages/Reporting/redux';
import LayoutAlert from './LayoutAlert';
import LayoutAlertPresence from './LayoutAlertPresence';

const DISMISS_STORAGE_KEY = 'storage_capacity_alert_dismissed_v1';
const DISMISS_STORAGE_KEY_LEGACY = 'logs_capacity_alert_dismissed_v2';
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const warnAccent = '#a67c00';
const criticalAccent = '#c62828';

type DismissState = { warnUntil: number; criticalUntil: number };

const emptyDismissState = (): DismissState => ({ warnUntil: 0, criticalUntil: 0 });

const readDismissState = (): DismissState => {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY)
      || localStorage.getItem(DISMISS_STORAGE_KEY_LEGACY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        warnUntil: Number(parsed?.warnUntil) || 0,
        criticalUntil: Number(parsed?.criticalUntil) || 0,
      };
    }
  } catch {
    /* ignore */
  }
  return emptyDismissState();
};

const StorageCapacityAlert = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const rooms = useAppSelector(selectRooms);
  const permission = useAppSelector(selectPermission);
  const liveView = useAppSelector(selectLiveView);
  const userEmail = useAppSelector(selectUserEmail);
  const managerReports = useAppSelector(selectManagerReports);
  const rightBarDismissTick = useAppSelector(selectRightBarDismissTick);
  const { isHomePath } = usePath();
  const [dismissState, setDismissState] = useState(readDismissState);
  const { dbBytes, dbMaxBytes, dbTone: tone, hasPlan } = useHotelStorage();

  // Include session mock settles so storage alert can show after alert pay
  const sessionDismissedIds = rightBarDismissTick >= 0
    ? readLiveDismissedRoomChargeIds()
    : undefined;
  const roomChargeActive = isCheckoutRoomChargeAlertActive(
    rooms,
    permission,
    isHomePath,
    Date.now(),
    sessionDismissedIds,
    liveView,
  );
  // Report alerts outrank storage (MB) alerts.
  const managerReportsActive = isManagerReportsAlertActive(
    managerReports,
    isHomePath,
    userEmail,
  );

  const canSee = (permission || 0) >= PERM.SUB_MANAGER;
  const dismissedUntil = tone === 'critical'
    ? dismissState.criticalUntil
    : dismissState.warnUntil;
  const isDismissed = !!tone && Date.now() < dismissedUntil;

  const open = (
    !isHomePath
    && canSee
    && !roomChargeActive
    && !managerReportsActive
    && !!tone
    && !isDismissed
  );

  const remaining = Math.max(0, dbMaxBytes - dbBytes);
  const isCritical = tone === 'critical';
  const accent = isCritical ? criticalAccent : warnAccent;

  const onGotIt = () => {
    const until = Date.now() + DISMISS_MS;
    const next: DismissState = {
      ...dismissState,
      ...(tone === 'critical' ? { criticalUntil: until } : { warnUntil: until }),
    };
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setDismissState(next);
  };

  const storageKind = hasPlan ? 'storage' : 'database storage';

  return (
    <LayoutAlertPresence open={open}>
      {tone ? (
        <LayoutAlert
          accent={accent}
          icon={isCritical
            ? <DeleteSweep fontSize="small" />
            : <WarningAmber fontSize="small" />}
          title={isCritical ? 'Storage full' : 'Storage running low'}
          text={isCritical
            ? `Your property has reached its ${formatStorageBytes(dbMaxBytes)} storage limit. Oldest activity logs and data are being deleted as new entries are added.`
            : `Your property has ${formatStorageBytes(remaining)} of ${formatStorageBytes(dbMaxBytes)} ${storageKind} left. Capacity will soon run out.`}
          primaryVariant="outlined"
          secondaryAction={{ label: 'Got it', onClick: onGotIt }}
          primaryAction={{
            label: 'Increase storage',
            onClick: () => history.push(
              erpSettingsPathFromPathname(pathname, 'subscription', 'storage'),
            ),
          }}
        />
      ) : null}
    </LayoutAlertPresence>
  );
};

export default StorageCapacityAlert;
